import { useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { useUpdateWorkAssignmentListAttachment } from 'components/IAP/hooks/assignmentListHooks';
import { useGetDraftRosterMembersForIncident } from 'components/IAP/hooks/incidentRoster/useGetDraftRosterMembersForIncident';
import {
  useGetPositionsForIncident,
  useGetRosterForIncident,
} from 'hooks/incident/useRoster';
import type { AssignmentListRow } from './AssignmentContainer';
import { ICS204_OUTLINE_BTN } from './assignmentCardStyles';
import { Ics204RichTextField } from './Ics204Fields';
import Ics204LocationSelect from './Ics204LocationSelect';
import type { Ics204EditingSection } from './ics204Sections';
import { ics204HtmlToPlainText } from './ics204RichText';
import { PersonnelSectionTableEdit } from './PersonnelSectionTable';
import {
  buildRosterNameOptions,
  findRosterContactForName,
  type PersonnelContactField,
  type PersonnelNameField,
} from './personnelSection';

type SectionEditorProps = {
  item: AssignmentListRow;
  periodNumber: number;
  /** Called after a successful save (explicit Save or outside-click autosave). */
  onDone: () => void;
  /** Called when the user discards without saving. */
  onCancel: () => void;
};

const SectionActions = ({
  isSaving,
  onCancel,
}: {
  isSaving: boolean;
  onCancel: () => void;
}) => (
  <div className="tw:mt-3 tw:flex tw:items-center tw:gap-2">
    <Button type="submit" size="sm" disabled={isSaving}>
      {isSaving ? 'Saving…' : 'Save'}
    </Button>
    <Button
      type="button"
      size="sm"
      variant="outline"
      className={ICS204_OUTLINE_BTN}
      disabled={isSaving}
      onClick={onCancel}
    >
      Cancel
    </Button>
  </div>
);

export const AssignedLocationSectionEdit = ({
  item,
  onDone,
  onCancel,
}: SectionEditorProps) => {
  const { data: positions } = useGetPositionsForIncident();
  const { mutateAsync: updateWala } = useUpdateWorkAssignmentListAttachment();
  const [isSaving, setIsSaving] = useState(false);
  const form = useForm({
    defaultValues: {
      branch: item.branch ?? '',
      division: item.division ?? '',
      walaGroup: item.walaGroup ?? '',
      stagingArea: item.stagingArea ?? '',
    },
  });

  return (
    <FormProvider {...form}>
      <form
        data-testid="ics204-section-edit-assignedLocation"
        onSubmit={form.handleSubmit(async (data) => {
          if (!item.walaId) return;
          setIsSaving(true);
          try {
            await updateWala({
              walaId: item.walaId,
              branch: data.branch,
              division: data.division,
              walaGroup: data.walaGroup,
              stagingArea: data.stagingArea,
              touch: true,
            });
            onDone();
          } catch {
            // Mutation hooks toast; keep the form open for retry.
          } finally {
            setIsSaving(false);
          }
        })}
      >
        <div className="tw:grid tw:min-w-0 tw:grid-cols-1 tw:gap-4 tw:md:grid-cols-2">
          <Ics204LocationSelect
            label="Branch"
            fieldName="branch"
            testId="ics204-edit-branch"
            category="Branch"
            positions={positions ?? []}
            className="tw:min-w-0"
          />
          <Ics204LocationSelect
            label="Division"
            fieldName="division"
            testId="ics204-edit-division"
            category="Division"
            positions={positions ?? []}
            className="tw:min-w-0"
          />
          <Ics204LocationSelect
            label="Group"
            fieldName="walaGroup"
            testId="ics204-edit-group"
            category="Group"
            positions={positions ?? []}
            className="tw:min-w-0"
          />
          <Ics204LocationSelect
            label="Staging Area"
            fieldName="stagingArea"
            testId="ics204-edit-staging-area"
            category="Staging Area"
            positions={positions ?? []}
            className="tw:min-w-0"
          />
        </div>
        <SectionActions isSaving={isSaving} onCancel={onCancel} />
      </form>
    </FormProvider>
  );
};

export const PersonnelSectionEdit = ({
  item,
  onDone,
  onCancel,
}: SectionEditorProps) => {
  const { data: roster } = useGetRosterForIncident();
  const { data: draftRoster } = useGetDraftRosterMembersForIncident();
  const { mutateAsync: updateWala } = useUpdateWorkAssignmentListAttachment();
  const [isSaving, setIsSaving] = useState(false);
  const rosterNameOptions = useMemo(
    () =>
      buildRosterNameOptions(roster, draftRoster, [
        item.sectionChief,
        item.branchDirector,
        item.divisionGroupSupervisor,
      ]),
    [
      roster,
      draftRoster,
      item.sectionChief,
      item.branchDirector,
      item.divisionGroupSupervisor,
    ]
  );
  const form = useForm({
    defaultValues: {
      sectionChief: item.sectionChief ?? '',
      sectionChiefContact: item.sectionChiefContact ?? '',
      branchDirector: item.branchDirector ?? '',
      branchDirectorContact: item.branchDirectorContact ?? '',
      divisionGroupSupervisor: item.divisionGroupSupervisor ?? '',
      divisionGroupSupervisorContact: item.divisionGroupSupervisorContact ?? '',
    },
  });

  const handlePersonnelNameSelect = (
    _nameField: PersonnelNameField,
    contactField: PersonnelContactField,
    nextName: string
  ) => {
    const contact = findRosterContactForName(nextName, roster, draftRoster);
    form.setValue(contactField, contact, { shouldDirty: true });
  };

  return (
    <FormProvider {...form}>
      <form
        data-testid="ics204-section-edit-personnel"
        onSubmit={form.handleSubmit(async (data) => {
          if (!item.walaId) return;
          setIsSaving(true);
          try {
            await updateWala({
              walaId: item.walaId,
              ...data,
              touch: true,
            });
            onDone();
          } catch {
            // Mutation hooks toast; keep the form open for retry.
          } finally {
            setIsSaving(false);
          }
        })}
      >
        <PersonnelSectionTableEdit
          options={rosterNameOptions}
          onNameSelect={handlePersonnelNameSelect}
        />
        <SectionActions isSaving={isSaving} onCancel={onCancel} />
      </form>
    </FormProvider>
  );
};

export const SpecialInstructionsSectionEdit = ({
  item,
  onDone,
  onCancel,
}: SectionEditorProps) => {
  const { mutateAsync: updateWala } = useUpdateWorkAssignmentListAttachment();
  const [isSaving, setIsSaving] = useState(false);
  const form = useForm({
    defaultValues: {
      specialInstructions: item.specialInstructions ?? '',
    },
  });

  return (
    <FormProvider {...form}>
      <form
        data-testid="ics204-section-edit-specialInstructions"
        onSubmit={form.handleSubmit(async (data) => {
          if (!item.walaId) return;
          setIsSaving(true);
          try {
            await updateWala({
              walaId: item.walaId,
              specialInstructions: data.specialInstructions,
              touch: true,
            });
            onDone();
          } catch {
            // Mutation hooks toast; keep the form open for retry.
          } finally {
            setIsSaving(false);
          }
        })}
      >
        <Ics204RichTextField
          fieldName="specialInstructions"
          testId="ics204-edit-special-instructions"
          rules={{
            validate: (value: string) => {
              const plain = ics204HtmlToPlainText(value);
              if (plain.length > 1000) {
                return 'This maximum length is 1000 characters';
              }
              return true;
            },
          }}
        />
        <SectionActions isSaving={isSaving} onCancel={onCancel} />
      </form>
    </FormProvider>
  );
};

export const renderIcs204SectionEditor = (
  section: Ics204EditingSection,
  props: SectionEditorProps
) => {
  switch (section) {
    case 'assignedLocation':
      return <AssignedLocationSectionEdit {...props} />;
    case 'personnel':
      return <PersonnelSectionEdit {...props} />;
    case 'specialInstructions':
      return <SpecialInstructionsSectionEdit {...props} />;
    default:
      return null;
  }
};
